import { useEffect, useRef } from 'react'
import type { TranscriptSegment } from '@yui-receipt/shared'

type Props = {
  open: boolean
  onClose: () => void
  /** Stream URL for the visit audio. */
  audioUrl: string | null
  /** Phrase to highlight in the transcript (verbatim Thai ≤30 char). */
  sourcePhrase: string | null
  /** Seconds offset to seek and auto-play for ~3s. */
  transcriptStart: number | null
  /** Whole transcript for highlighting context. */
  transcript: string
  /** Segments for precise highlighting, if available. */
  segments?: TranscriptSegment[]
  label?: string
}

const AUTOPLAY_WINDOW_SEC = 3

/**
 * Bottom-sheet that shows the transcript excerpt supporting a SOAP field,
 * with the source phrase highlighted and the audio seeked to the field's
 * `transcriptStart`. Auto-plays a 3-second window then pauses — therapists
 * can replay via the native audio controls.
 */
export default function LinkedEvidenceSheet({
  open,
  onClose,
  audioUrl,
  sourcePhrase,
  transcriptStart,
  transcript,
  segments,
  label,
}: Props) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const autoPauseTimer = useRef<number | null>(null)

  // Seek + play when the sheet opens.
  useEffect(() => {
    if (!open) return
    const a = audioRef.current
    if (!a) return
    const play = () => {
      if (typeof transcriptStart === 'number' && transcriptStart >= 0) {
        try {
          a.currentTime = transcriptStart
        } catch {
          // iOS may throw if metadata isn't loaded yet — retry on loadedmetadata.
        }
      }
      a.play().catch(() => {
        // Autoplay policy blocks — therapist will press play manually.
      })
      if (autoPauseTimer.current != null) window.clearTimeout(autoPauseTimer.current)
      autoPauseTimer.current = window.setTimeout(() => {
        try {
          a.pause()
        } catch {
          // ignore
        }
      }, AUTOPLAY_WINDOW_SEC * 1000)
    }
    if (a.readyState >= 1) play()
    else a.addEventListener('loadedmetadata', play, { once: true })
    return () => {
      if (autoPauseTimer.current != null) {
        window.clearTimeout(autoPauseTimer.current)
        autoPauseTimer.current = null
      }
      try {
        a.pause()
      } catch {
        // ignore
      }
    }
  }, [open, transcriptStart, audioUrl])

  // Close on escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const highlighted = highlightTranscript(transcript, sourcePhrase, segments, transcriptStart)

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label={label ? `ต้นฉบับสำหรับ ${label}` : 'ต้นฉบับจากการอัดเสียง'}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            🔊 ต้นฉบับ{label ? ` — ${label}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost min-h-tap text-sm"
            aria-label="ปิด"
          >
            ปิด
          </button>
        </div>

        <div className="px-4 py-3 border-b border-slate-200">
          {audioUrl ? (
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              preload="metadata"
              className="w-full"
            />
          ) : (
            <p className="text-sm text-slate-500">
              ไม่พบไฟล์เสียง (อาจถูกลบหลัง 90 วันตามนโยบาย PDPA)
            </p>
          )}
        </div>

        <div className="px-4 py-3 overflow-y-auto text-sm leading-relaxed">
          {highlighted}
        </div>
      </div>
    </div>
  )
}

/**
 * Produce React nodes highlighting the source phrase inside the full transcript.
 * Falls back to segment-level highlighting when the verbatim phrase isn't found.
 */
function highlightTranscript(
  full: string,
  phrase: string | null,
  segments: TranscriptSegment[] | undefined,
  startSec: number | null,
) {
  if (phrase && phrase.trim().length > 0 && full.includes(phrase)) {
    const idx = full.indexOf(phrase)
    return (
      <p className="text-slate-700">
        <span>{full.slice(0, idx)}</span>
        <mark className="bg-yellow-200 text-slate-900 rounded px-0.5">
          {full.slice(idx, idx + phrase.length)}
        </mark>
        <span>{full.slice(idx + phrase.length)}</span>
      </p>
    )
  }
  if (segments && segments.length && typeof startSec === 'number') {
    return (
      <div className="space-y-1">
        {segments.map((seg, i) => {
          const hit =
            seg.startSec <= startSec && startSec <= seg.endSec + 0.25
          return (
            <span
              key={i}
              className={
                hit
                  ? 'bg-yellow-200 text-slate-900 rounded px-0.5'
                  : 'text-slate-600'
              }
            >
              {seg.text}{' '}
            </span>
          )
        })}
      </div>
    )
  }
  return <p className="text-slate-700 whitespace-pre-wrap">{full}</p>
}
